import { Link } from 'react-router-dom'

import { getHrefWithThreadPreservation } from '@/utils/thread-navigation'
import { MapPin } from 'lucide-react'

import { JobBoardSaveButton } from '@/components/candidate/job-board-save-button'
import { PUBLIC_CARD_SURFACE_INTERACTIVE } from '@/components/public/public-surfaces'
import { Badge } from '@/components/ui/badge'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import type { PublicJob } from '@/types/public-share'
import { formatPublicJobMeta, formatPublicJobPostedLabel } from '@/utils/public-job-meta-display'
import { isPublicOpenRoleNew } from '@/utils/public-open-role-new'

type PublicOpenRoleCardProps = {
  job: PublicJob
  saved?: boolean
  isSaving?: boolean
  onSaveToggle?: () => void
  /** Defaults to the guest share URL `/jobs/:slug`. */
  to?: string
  linkState?: unknown
}

export function PublicOpenRoleCard({
  job,
  saved = false,
  isSaving = false,
  onSaveToggle,
  to,
  linkState,
}: PublicOpenRoleCardProps) {
  const meta = formatPublicJobMeta(job, { includeSalary: true })
  const posted = formatPublicJobPostedLabel(job)
  const jobHref = to || `/jobs/${job.slug}`
  const isNew = isPublicOpenRoleNew(job.published_at)
  const showSave = onSaveToggle != null

  return (
    <Card className={PUBLIC_CARD_SURFACE_INTERACTIVE}>
      <Link
        to={getHrefWithThreadPreservation(jobHref)}
        state={linkState}
        className="absolute inset-0 z-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={isNew ? `View ${job.title} (new)` : `View ${job.title}`}
      />
      <CardHeader className="relative z-10 gap-2 pointer-events-none">
        <div className="flex min-w-0 flex-wrap items-center gap-4">
          <CardTitle className="text-base">{job.title}</CardTitle>
          {isNew ? (
            <Badge variant="warningSoft" className="shrink-0 font-medium" aria-hidden>
              New
            </Badge>
          ) : null}
        </div>
        {meta ? (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 shrink-0 translate-y-px" aria-hidden />
            {meta}
          </p>
        ) : null}
        {posted || showSave ? (
          <div className="flex min-w-0 items-center justify-between gap-4">
            {posted ? <p className="text-sm text-muted-foreground">{posted}</p> : <span />}
            {showSave ? (
              <div
                className="pointer-events-auto relative z-10 shrink-0"
                onPointerDown={(event) => event.stopPropagation()}
              >
                <JobBoardSaveButton
                  saved={saved}
                  disabled={isSaving}
                  size="lg"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    onSaveToggle()
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </CardHeader>
    </Card>
  )
}
